import { Menu, MenuItem, PopoverPosition } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useNotification } from "app/NotificationProvider";
import { AnalyticsEvents, mixpanel } from "common/analytics";
import { useTranslation } from "common/locales";
import useConfirmation from "common/utils/useConfirmation";
import {
  ArableField,
  Field,
  FIELD_TYPE,
  useArableField,
  useDeleteArableField,
  useDeleteField,
  useField,
} from "model";
import { useNavigate } from "react-router-dom";
import { changeArableFieldVisibility, changeFieldVisibility } from "services";
import { useAppStore, useUIStore } from "stores";

interface FieldEditActionMenuProps {
  fieldId: string;
  anchor?: null | HTMLElement;
  anchorPosition?: PopoverPosition;
  close: () => void;
  noEdit?: boolean;
  noDelete?: boolean;
  fieldType: FIELD_TYPE;
}

export default function FieldEditActionMenu({
  fieldId,
  anchor,
  anchorPosition,
  close,
  noEdit,
  noDelete,
  fieldType,
}: FieldEditActionMenuProps) {
  // Use React Query to fetch field data
  const { data: grassField } = useField(
    fieldType === FIELD_TYPE.GRASS ? fieldId : undefined
  );
  const { data: arableField } = useArableField(
    fieldType !== FIELD_TYPE.GRASS ? fieldId : undefined
  );
  const currentField =
    fieldType === FIELD_TYPE.GRASS ? grassField : arableField;

  // Use React Query mutations for deletion (ensures cache invalidation)
  const deleteFieldMutation = useDeleteField();
  const deleteArableFieldMutation = useDeleteArableField();

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { notify } = useNotification();

  const confirmDeletion = useConfirmation({
    title: t("confirmDeleteFieldDialog.title"),
    description: t("confirmDeleteFieldDialog.text", {
      fieldName: currentField?.name,
    }),
  });

  // Use Zustand for current farm ID and editing state
  const currentFarmId = useAppStore((state) => state.currentFarmId);
  const setIsEditing = useUIStore((state) => state.setIsEditing);
  const queryClient = useQueryClient();

  return (
    <div>
      <Menu
        id="simple-menu"
        anchorEl={anchor}
        anchorPosition={anchorPosition}
        anchorReference={
          anchor ? "anchorEl" : anchorPosition ? "anchorPosition" : "none"
        }
        keepMounted
        open={Boolean(anchor || anchorPosition)}
        onClose={close}
      >
        {!noEdit && (
          <MenuItem onClick={edit}>{t("fieldlistmenu.edit.label")}</MenuItem>
        )}
        {!noDelete && (
          <MenuItem onClick={onDelete}>
            {t("fieldlistmenu.delete.label")}
          </MenuItem>
        )}
      </Menu>
    </div>
  );

  async function onDelete() {
    const confirmed = await confirmDeletion();
    close();
    if (!confirmed || !currentField) return;
    // Use React Query mutations for deletion (ensures cache invalidation)
    if (fieldType === FIELD_TYPE.GRASS) {
      deleteFieldMutation.mutate(currentField as Field);
    } else {
      deleteArableFieldMutation.mutate(currentField as ArableField);
    }
    mixpanel.track(AnalyticsEvents.FIELD_DELETED);
    navigate(`/farm/${currentFarmId || ""}`, { replace: true });
  }

  function edit() {
    close();
    try {
      setIsEditing(true);
      if (fieldType === FIELD_TYPE.GRASS) {
        changeFieldVisibility(
          { guid: fieldId, visibility: "none" },
          queryClient
        );
      } else {
        changeArableFieldVisibility(
          { guid: fieldId, visibility: "none" },
          queryClient
        );
      }
      mixpanel.track(AnalyticsEvents.FIELD_UPDATE_STARTED);
      navigate(`/edit-field/${fieldId}/enter-details`);
    } catch (e) {
      mixpanel.track(AnalyticsEvents.FIELD_UPDATE_FAILED);
      notify(
        t("fieldlistmenu.notification.editFailed", {
          fieldName: currentField?.name,
        }),
        "error"
      );
    }
  }
}
